@if (
  isStandaloneTaskList() || !(workContextService.isContextChangingWithDelay$ | async)
) {
  <div
    #containerEl
    class="wrapper"
    cdkDropListGroup
    @projectChange
  >
    <div
      #splitTopEl
      class="today"
      cdkScrollable
    >
      @if (!pluginEmbedId()) {
        <!-- Sticky list toolbar. Rows scroll under it, so it carries a blur
             and a hairline rather than an opaque band. -->
        <!-- Selection bar. Replaces the toolbar's contents while a
             multi-selection is live, so the actions land where the user is
             already looking instead of in a floating overlay. -->
        @if (taskSelectionService.isMultiSelectActive()) {
          <header class="list-toolbar list-toolbar--selection">
            <span class="selection-count">{{
              T.WW.N_SELECTED | translate: { nr: taskSelectionService.count() }
            }}</span>

            <div class="selection-actions">
              <button
                class="selection-btn"
                (click)="taskSelectionService.markDone()"
                type="button"
              >
                <mat-icon>check_circle</mat-icon>{{ T.WW.SELECTION_DONE | translate }}
              </button>
              <button
                class="selection-btn"
                (click)="taskSelectionService.addToToday()"
                type="button"
              >
                <mat-icon>wb_sunny</mat-icon>{{ T.WW.SELECTION_ADD_TODAY | translate }}
              </button>
              @if (isOnTodayList()) {
                <button
                  class="selection-btn"
                  (click)="taskSelectionService.removeFromToday()"
                  type="button"
                >
                  <mat-icon>wb_twilight</mat-icon
                  >{{ T.WW.SELECTION_REMOVE_TODAY | translate }}
                </button>
              }
              <button
                class="selection-btn selection-btn--danger"
                (click)="taskSelectionService.delete()"
                type="button"
              >
                <mat-icon>delete</mat-icon>{{ T.WW.SELECTION_DELETE | translate }}
              </button>
            </div>

            <button
              class="selection-clear"
              (click)="taskSelectionService.clear()"
              type="button"
            >
              {{ T.WW.SELECTION_CLEAR | translate }}
            </button>
          </header>
        } @else {
          <header class="list-toolbar">
            <div class="list-toolbar__headline">
              <span class="list-toolbar__title">{{
                workContextService.activeWorkContextTitle$ | async
              }}</span>
              @if (listMeta(); as meta) {
                <span class="list-toolbar__meta">{{ meta }}</span>
              }
            </div>

            <!-- SegmentedControl: an inset track, the active segment raised. -->
            <div
              class="density-switch"
              role="group"
            >
              @for (opt of densityOptions; track opt.id) {
                <button
                  class="density-switch__seg"
                  [class.isActive]="density() === opt.id"
                  (click)="setDensity(opt.id)"
                  type="button"
                >
                  {{ opt.label | translate }}
                </button>
              }
            </div>

            <!-- The one primary action in the view. `tour-addBtn` is the hook
                 the onboarding hint and the e2e suite target; it moved here
                 from the old header button, which this replaces. -->
            <button
              class="new-task-btn tour-addBtn"
              (click)="layoutService.showAddTaskBar()"
              type="button"
            >
              <mat-icon>add</mat-icon>{{ T.WW.NEW_TASK | translate }}
            </button>

            <ng-content select="[workViewHeaderAction]"></ng-content>
          </header>
        }
      }

      @if (pluginEmbedId(); as embedId) {
        <plugin-index
          [directPluginId]="embedId"
          [showFullUI]="false"
          [skipCleanupOnDestroy]="true"
        ></plugin-index>
      } @else {
        <div
          class="task-list-wrapper"
          [class.density-rows]="density() === 'rows'"
          [class.density-cards]="density() === 'cards'"
          [class.density-compact]="density() === 'compact'"
        >
          @if (!isHasTasksToWorkOn()) {
            <div
              @expandFade
              class="add-more-or-finish"
            >
              <div class="no-tasks-illu-wrapper">
                <div class="inline-block-container">
                  <img
                    class="show-light-only"
                    src="assets/illu/horizon.svg"
                    alt=""
                  />
                  <img
                    class="show-dark-only"
                    src="assets/illu/horizon-dark.svg"
                    alt=""
                  />
                </div>
                <h2 class="no-tasks-label">
                  @if (
                    hasDoneTasks() || (workContextService.doneTodayArchived$ | async)
                  ) {
                    {{ T.WW.NO_PLANNED_TASK_ALL_DONE | translate }}
                  } @else {
                    {{ T.WW.NO_PLANNED_TASKS | translate }}
                  }
                </h2>
              </div>

              @if (!isStandaloneTaskList() && !overdueTasks().length) {
                <div class="btn-wrapper">
                  <button
                    (click)="layoutService.showAddTaskBar()"
                    color=""
                    mat-stroked-button
                    tabindex="1"
                  >
                    <mat-icon>playlist_add</mat-icon>
                    {{ T.WW.ADD_MORE | translate }}
                  </button>
                </div>
              }
            </div>
          }
          @if (customizedUndoneTasksForView(); as customized) {
            @if (customized.grouped) {
              @for (group of customized.grouped | keyvalue; track group.key) {
                <collapsible
                  [title]="group.key | scheduledDateGroup"
                  [isIconBefore]="true"
                  [isExpanded]="
                    !customizerService.collapsedGroupIds().includes(group.key)
                  "
                  (isExpandedChange)="customizerService.toggleGroupExpansion(group.key)"
                  [isGroup]="true"
                >
                  <task-list
                    [tasks]="group.value"
                    [isSortingDisabled]="isMainListSortingDisabled()"
                    listId="PARENT"
                    [listModelId]="mainListModelId()"
                  ></task-list>
                </collapsible>
              }
            } @else if (visibleSections().length && !customizerService.isCustomized()) {
              @let bySection = undoneTasksBySection();
              <div
                class="sections-wrapper"
                cdkDropList
                [cdkDropListEnterPredicate]="acceptSectionDragOnly"
                (cdkDropListDropped)="dropSection($event)"
              >
                <div class="no-section">
                  <task-list
                    [tasks]="bySection.noSection"
                    [listId]="'PARENT'"
                    [listModelId]="'UNDONE'"
                    [noTasksMsg]="
                      undoneTasks().length
                        ? (T.WW.NO_TASKS_IN_MAIN_LIST | translate)
                        : undefined
                    "
                  ></task-list>
                </div>
                @for (section of visibleSections(); track section.id) {
                  <div
                    class="section-container"
                    cdkDrag
                    [cdkDragData]="section"
                    [cdkDragStartDelay]="dragDelayForTouch()"
                    [cdkDragLockAxis]="'y'"
                  >
                    <collapsible
                      [title]="section.title"
                      [isIconBefore]="true"
                      [isExpanded]="section.isExpanded"
                      (isExpandedChange)="
                        sectionService.updateSection(section.id, { isExpanded: $event })
                      "
                      [isTitleDragHandle]="true"
                    >
                      <ng-container actions>
                        @if (sectionHeaderMeta(section); as meta) {
                          @if (meta.deadlineDay || meta.value) {
                            <div class="section-meta">
                              @if (meta.deadlineDay) {
                                <span
                                  class="section-meta-item"
                                  [matTooltip]="T.WW.SECTION_DEADLINE | translate"
                                >
                                  <mat-icon>event</mat-icon>
                                  {{ meta.deadlineDay | localDateStr }}
                                </span>
                              }
                              @if (meta.value) {
                                <span
                                  class="section-meta-item"
                                  [matTooltip]="T.WW.SECTION_VALUE | translate"
                                >
                                  <mat-icon>hotel_class</mat-icon>
                                  {{ taskValueLabels[meta.value] }}
                                </span>
                              }
                            </div>
                          }
                        }
                        <button
                          mat-icon-button
                          [matMenuTriggerFor]="sectionMenu"
                          [attr.aria-label]="T.WW.SECTION_OPTIONS | translate"
                          (click)="$event.stopPropagation()"
                        >
                          <mat-icon>more_vert</mat-icon>
                        </button>
                        <mat-menu #sectionMenu="matMenu">
                          <button
                            mat-menu-item
                            (click)="editSection(section.id, section.title)"
                          >
                            <mat-icon>edit</mat-icon>
                            {{ T.G.EDIT | translate }}
                          </button>
                          <button
                            mat-menu-item
                            [matMenuTriggerFor]="sectionValueMenu"
                          >
                            <mat-icon>trending_up</mat-icon>
                            {{ T.WW.SECTION_VALUE | translate }}
                          </button>
                          <button
                            mat-menu-item
                            (click)="editSectionDeadline(section.id, section.deadlineDay)"
                          >
                            <mat-icon>event</mat-icon>
                            {{ T.WW.SECTION_DEADLINE | translate }}
                          </button>
                          <button
                            mat-menu-item
                            (click)="deleteSection(section.id)"
                          >
                            <mat-icon>delete</mat-icon>
                            {{ T.G.DELETE | translate }}
                          </button>
                        </mat-menu>
                        <mat-menu #sectionValueMenu="matMenu">
                          <button
                            mat-menu-item
                            (click)="setSectionValue(section.id, null)"
                          >
                            {{ T.F.TASK.CMP.CLEAR_VALUE | translate }}
                          </button>
                          @for (value of taskScoreLevels; track value) {
                            <button
                              mat-menu-item
                              (click)="setSectionValue(section.id, value)"
                            >
                              {{ taskValueLabels[value] }}
                            </button>
                          }
                        </mat-menu>
                      </ng-container>

                      <task-list
                        [tasks]="bySection.dict[section.id]"
                        [listId]="'PARENT'"
                        [listModelId]="section.id"
                      ></task-list>
                    </collapsible>
                  </div>
                }
              </div>
            } @else {
              <task-list
                class="tour-undoneList"
                [tasks]="customized.list"
                [isSortingDisabled]="isMainListSortingDisabled()"
                listId="PARENT"
                [listModelId]="mainListModelId()"
              ></task-list>
            }
          }

          @if (isShowOverduePanel()) {
            <section
              class="overdue-tasks"
              @expandFade
            >
              <!-- OVERDUE Outline -->
              <collapsible
                [title]="(T.G.OVERDUE | translate) + ' (' + overdueTasks().length + ')'"
                [isExpanded]="!isOverdueHidden()"
                (isExpandedChange)="isOverdueHidden.set(!$event)"
                [isIconBefore]="true"
                [isGroup]="true"
              >
                <div class="overdue-btn-wrapper">
                  <button
                    mat-stroked-button
                    color="primary"
                    tabindex="1"
                    (click)="addAllOverdueToMyDay()"
                  >
                    <mat-icon>wb_sunny</mat-icon>
                    {{ T.F.TASK.CMP.ADD_TO_MY_DAY | translate }}
                  </button>
                  <!--                <button-->
                  <!--                  mat-stroked-button-->
                  <!--                  color="primary"-->
                  <!--                  tabindex="1"-->
                  <!--                >-->
                  <!--                  <mat-icon>edit_calendar</mat-icon>-->
                  <!--                  {{ T.F.PLANNER.D.ADD_PLANNED.RE_PLAN_ALL | translate }}-->
                  <!--                </button>-->
                </div>

                <div>
                  <task-list
                    [tasks]="overdueTasks()"
                    [isSortingDisabled]="true"
                    listId="PARENT"
                    listModelId="OVERDUE"
                  ></task-list>
                </div>
              </collapsible>
            </section>
          }

          @if (
            !isStandaloneTaskList() && laterTodayTasks().length > 0 && isOnTodayList()
          ) {
            <div
              @expand
              class="collapsible-section"
            >
              <collapsible
                [title]="
                  (T.WW.LATER_TODAY | translate) +
                  ' (' +
                  (laterTodayTasks()?.length || 0) +
                  ')'
                "
                [isExpanded]="!isLaterTodayHidden()"
                (isExpandedChange)="isLaterTodayHidden.set(!$event)"
                [isIconBefore]="true"
                [isGroup]="true"
              >
                <task-list
                  [tasks]="laterTodayTasks()"
                  [isSortingDisabled]="true"
                  listId="PARENT"
                  listModelId="LATER_TODAY"
                ></task-list>
              </collapsible>
            </div>
          }

          @if (hasDoneTasks()) {
            @let nrOfDoneArchived =
              doneTasks()?.length === 0
                ? (workContextService.doneTodayArchived$ | async)
                : 0;
            <div
              @expand
              class="collapsible-section"
            >
              <collapsible
                [title]="
                  (T.WW.DONE_TASKS | translate) +
                  ' (' +
                  (doneTasks()?.length || 0) +
                  (nrOfDoneArchived ? ' + ' + nrOfDoneArchived : '') +
                  ')'
                "
                [isExpanded]="!isDoneHidden()"
                (isExpandedChange)="isDoneHidden.set(!$event)"
                [isIconBefore]="true"
                [isGroup]="true"
              >
                <task-list
                  class="tour-doneList"
                  [noTasksMsg]="
                    (nrOfDoneArchived ? T.WW.DONE_TASKS_IN_ARCHIVE : T.WW.NO_DONE_TASKS)
                      | translate
                  "
                  [tasks]="doneTasks()"
                  listId="PARENT"
                  listModelId="DONE"
                ></task-list>
                <div class="finish-day-button-wrapper">
                  @if (hasDoneTasks() && (!isOnTodayList() || !isFinishDayEnabled())) {
                    <button
                      class="e2e-move-done-to-archive"
                      mat-stroked-button
                      (click)="moveDoneToArchive()"
                      tabindex="1"
                    >
                      <mat-icon>done_all</mat-icon>
                      {{ T.WW.MOVE_DONE_TO_ARCHIVE | translate }}
                    </button>
                  }
                </div>
              </collapsible>
            </div>
          }

          @if (isShowRepeatCfgsPanel()) {
            <div
              @expand
              class="collapsible-section"
            >
              <collapsible
                [title]="
                  (T.WW.RECURRING_TASKS | translate) +
                  ' (' +
                  repeatCfgsForContext().length +
                  ')'
                "
                [isExpanded]="!isRepeatCfgsHidden()"
                (isExpandedChange)="isRepeatCfgsHidden.set(!$event)"
                [isIconBefore]="true"
                [isGroup]="true"
              >
                <div class="repeat-cfg-list">
                  @for (repeatCfg of repeatCfgsForContext(); track repeatCfg.id) {
                    <repeat-cfg-preview [repeatCfg]="repeatCfg"></repeat-cfg-preview>
                  }
                </div>
              </collapsible>
            </div>
          }

          @if (!isStandaloneTaskList() && isOnTodayList() && isFinishDayEnabled()) {
            <finish-day-btn [hasDoneTasks]="hasDoneTasks()"></finish-day-btn>
          }
        </div>
      }
    </div>

    @if (isShowBacklog() && !pluginEmbedId()) {
      <div
        #splitBottomEl
        [style.user-focus]="splitInputPos === 100 ? 'none' : ''"
        class="backlog"
      >
        <split
          (posChanged)="splitInputPos = $event"
          [containerEl]="containerEl"
          [counter]="backlogTasks()?.length"
          [isAnimateBtn]="isTriggerBacklogIconAni$ | async"
          [splitBottomEl]="splitBottomEl"
          [splitPos]="splitInputPos"
          [splitTopEl]="splitTopEl"
        ></split>
        @if (splitInputPos !== 100) {
          <div
            class="inner-wrapper"
            style="user-select: none"
          >
            <backlog
              (closeBacklog)="splitInputPos = 100"
              [backlogTasks]="backlogTasks()"
            ></backlog>
          </div>
        }
      </div>
    }
  </div>
}
